/** Authenticated application pages exposed through stable browser URLs. */
export type DashboardSection =
  | "dashboard"
  | "positions"
  | "orders"
  | "backtests"
  | "api-keys"
  | "settings"
  | "admin";

/** One authenticated navigation destination. */
export interface DashboardNavigationItem {
  /** Stable page identifier. */
  section: DashboardSection;
  /** Browser URL for the page. */
  path: string;
  /** Visible navigation label. */
  label: string;
}

/** Ordered authenticated application navigation. */
export const DASHBOARD_NAVIGATION: readonly DashboardNavigationItem[] = [
  { section: "dashboard", path: "/dashboard", label: "Dashboard" },
  { section: "positions", path: "/positions", label: "Positions" },
  { section: "orders", path: "/orders", label: "Orders" },
  { section: "backtests", path: "/backtests", label: "Backtests" },
  { section: "api-keys", path: "/api-keys", label: "API keys" },
  { section: "settings", path: "/settings", label: "Settings" },
  { section: "admin", path: "/admin", label: "Administration" },
];

/**
 * Resolves an authenticated browser path to its application page.
 *
 * The root path remains a backwards-compatible dashboard entry point.
 *
 * @param pathname Browser pathname.
 * @returns Matching dashboard section or null for a non-application path.
 */
export function resolveDashboardSection(
  pathname: string,
): DashboardSection | null {
  const normalized = pathname.replace(/\/+$/u, "") || "/";
  if (normalized === "/") {
    return "dashboard";
  }
  return (
    DASHBOARD_NAVIGATION.find((item) => item.path === normalized)?.section ??
    null
  );
}

/**
 * Returns the canonical URL for one authenticated application page.
 *
 * @param section Dashboard section.
 * @returns Canonical browser path.
 */
export function getDashboardSectionPath(section: DashboardSection): string {
  const item = DASHBOARD_NAVIGATION.find(
    (candidate) => candidate.section === section,
  );
  if (item === undefined) {
    throw new Error(`No dashboard path is configured for ${section}.`);
  }
  return item.path;
}

/**
 * Returns the visible label for one authenticated application page.
 *
 * @param section Dashboard section.
 * @returns Navigation label.
 */
export function getDashboardSectionLabel(section: DashboardSection): string {
  const item = DASHBOARD_NAVIGATION.find(
    (candidate) => candidate.section === section,
  );
  if (item === undefined) {
    throw new Error(`No dashboard label is configured for ${section}.`);
  }
  return item.label;
}
